import React, { useEffect, useMemo } from "react";
import { showDialogAsync } from "../../components/dialog-queue";
import { AlertDialog } from "../../components/dialog/alert";
import { ConfirmDialog } from "../../components/dialog/confirm";
import { showLoginDialogAsync } from "../../components/dialogs/login-dialog";
import { useBusyBlockerContext } from "../../contexts/busy-blocker-context";
import { useAppContext } from "../useAppContext";
import { useClient } from "../useClient";
import { useEditor } from "../useEditor";
import { useErrorHandling } from "../useErrorHandling";
import { useNavigateToUrl } from "../useNavigate";
import { labels } from "./labels";

export const useLoginManager = () => {
	const client = useClient();
	const { context, googleAnalytics } = useAppContext();
	const { createFatalErrorHandler } = useErrorHandling();
	const editor = useEditor();

	const navigateToUrl = useNavigateToUrl();
	const { busyBlocker: blockInputEvents } = useBusyBlockerContext();

	const loginManager = useMemo(() => {
		return new eXpress.creator.LoginManager(
			{
				checkAccountValidityAsync: client.checkAccountValidityAsync.bind(client),
				getAccount: () => ({
					id: context.account.id,
					type: context.account.type,
				}),
				errorHandlerCreator: () => createFatalErrorHandler(),
				loginUserAsync: () =>
					showLoginDialogAsync(undefined, {
						editor,
					}).then(() => {
						return context.account;
					}),
				navigateToUrl: (url) => navigateToUrl(url),
				blockInputEvents,
				ga: googleAnalytics,
				showAlertDialogAsync: ({ message, title, ok }) =>
					showDialogAsync(({ resolve }, { isOpen }) => (
						<AlertDialog
							titleLabel={title}
							messageLabel={message}
							isOpen={isOpen}
							okLabel={ok}
							onOk={() => resolve()}
						/>
					)),
				showConfirmDialogAsync: (options) =>
					showDialogAsync(({ resolve }, { isOpen }) => (
						<ConfirmDialog
							cancelLabel={options.noText}
							titleLabel={options.title}
							messageHtmlLabel={options.message}
							isOpen={isOpen}
							okLabel={options.yesText}
							onOk={() => resolve(true)}
							onCancel={() => resolve(false)}
						/>
					)),
			},
			{
				// not sure if this is ok? Maybe it needs to be implemented in the future
				canShowPopup: () => !blockInputEvents.isOpened(),
				labels,
			},
		);
	}, [
		blockInputEvents,
		client,
		context,
		createFatalErrorHandler,
		editor,
		googleAnalytics,
		navigateToUrl,
	]);

	useEffect(() => {
		return () => loginManager.dispose();
	}, [loginManager]);

	return loginManager;
};
